import { readSessionCookie } from "@server/auth/cookie-names";
import { parseColorScheme } from "@server/color-scheme";
import { getPublicEnv } from "@server/env";
import { requestMiddleware } from "@server/http";
import { getSession } from "@server/lib/permissions";
import { useEffect, useState } from "react";
import {
	data,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLocation,
} from "react-router";
import { getToast } from "remix-toast";
import { toast as notify, Toaster } from "sonner";
import { AppSidebar } from "~/components/app-sidebar";
import SafeRateLogo from "~/components/general/SafeRateLogo";
import SiteFooter from "~/components/general/SiteFooter";
import { GeneralErrorBoundary } from "~/components/general/ErrorBoundary";
import {
	SidebarInset,
	SidebarProvider,
	SidebarTrigger,
} from "~/components/ui/sidebar";
import { useNonce } from "~/hooks/useNonce";
import {
	ColorSchemeScript,
	useColorScheme,
} from "~/services/color-scheme/components";
import type { Route } from "./+types/root";
import stylesheet from "./styles/main.css?url";
import printStylesheet from "./styles/print.css?url";

type UserRole = "borrower" | "servicer_staff" | "originator_staff" | "investor";

export const links: Route.LinksFunction = () => [
	{
		as: "font",
		crossOrigin: "anonymous",
		href: "/fonts/ttnorms-regular-webfont.woff",
		rel: "preload",
		type: "font/woff",
	},
	{ href: printStylesheet, media: "print", rel: "stylesheet" },
];

export const meta: Route.MetaFunction = () => {
	return [
		{ title: "SafeRate Servicing" },
		{
			content:
				"Manage your mortgage payments, view loan details, and track your home financing with SafeRate Servicing.",
			name: "description",
		},
	];
};

export async function loader({ request, context }: Route.LoaderArgs) {
	await requestMiddleware(request);
	const colorScheme = await parseColorScheme(request);
	const { toast, headers } = await getToast(request);

	// Get token from Better Auth cookie
	const cookieHeader = request.headers.get("Cookie");
	const cookies =
		cookieHeader?.split(";").reduce(
			(acc, cookie) => {
				const parts = cookie.trim().split("=");
				const key = parts[0];
				const value = parts[1];
				if (key) {
					acc[key] = value ?? "";
				}
				return acc;
			},
			{} as Record<string, string>,
		) ?? {};
	const token = readSessionCookie(cookies);

	// Get session from database if token exists
	type UserRecord = Awaited<ReturnType<typeof context.db.query.user.findFirst>>;
	let authSession: Awaited<ReturnType<typeof getSession>> = null;
	let user: UserRecord = undefined;
	let roles: UserRole[] = [];

	if (token) {
		try {
			const session = await getSession(context.db, { token });
			authSession = session;
			if (session) {
				// Get user data from Better Auth user table
				const userRecord = await context.db.query.user.findFirst({
					where: (users, { eq }) => eq(users.id, session.userId),
				});

				if (userRecord) {
					user = userRecord;
					// Get roles from Better Auth user (already JSON in database)
					roles = Array.isArray(userRecord.roles) ? userRecord.roles : [];
				}
			}
		} catch (error) {
			console.error("Error loading session:", error);
		}
	}

	// Get active role from cookie (for persistence across navigation)
	const activeRoleCookie = cookies["active-role"] as UserRole | undefined;
	// Use cookie value if valid, otherwise default to first role
	const initialActiveRole =
		activeRoleCookie && roles.includes(activeRoleCookie)
			? activeRoleCookie
			: roles[0] || "borrower";

	return data(
		{
			colorScheme,
			ENV: getPublicEnv(),
			initialActiveRole,
			roles,
			toast,
			user,
		},
		{ headers },
	);
}

export function Layout({ children }: { children: React.ReactNode }) {
	const nonce = useNonce();
	const colorScheme = useColorScheme();

	return (
		<html
			className={`${colorScheme === "dark" ? "dark" : ""} touch-manipulation overflow-x-hidden`}
			lang="en"
			suppressHydrationWarning
		>
			<head>
				<meta charSet="utf-8" />
				<meta
					content="width=device-width, initial-scale=1.0"
					name="viewport"
				/>
				<Meta />
				<Links />
				<ColorSchemeScript nonce={nonce} />
				<link href={stylesheet} precedence="high" rel="stylesheet" />
			</head>
			<body>
				{children}
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
				<Toaster position="top-center" theme={colorScheme} />
			</body>
		</html>
	);
}

export default function App({ loaderData }: Route.ComponentProps) {
	const { ENV, initialActiveRole, roles, toast, user } = loaderData;
	const nonce = useNonce();
	const [activeRole, setActiveRole] = useState<UserRole>(initialActiveRole);
	const location = useLocation();

	useEffect(() => {
		if (toast?.type === "error") {
			notify.error(toast.message);
		}
		if (toast?.type === "success") {
			notify.success(toast.message);
		}
	}, [toast]);

	// No sidebar for unauthenticated users, nor on the sign-in and two-step
	// pages, which a signed-in staff member still passes through and which
	// draw their own full-screen layout (AuthShell).
	if (!user || location.pathname.startsWith("/auth/")) {
		return (
			<>
				<Outlet />
				<script
					// biome-ignore lint/security/noDangerouslySetInnerHtml: false positive
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(ENV)}`,
					}}
					nonce={nonce}
				/>
			</>
		);
	}

	// Authenticated users get sidebar
	return (
		<SidebarProvider>
			<AppSidebar
				activeRole={activeRole}
				onRoleChange={setActiveRole}
				user={{
					avatar: user.image || undefined,
					email: user.email,
					name: user.name,
					roles,
				}}
			/>
			<SidebarInset>
				<header className="sticky top-0 z-10 flex h-14 shrink-0 items-center gap-3 border-b bg-background/90 px-4 backdrop-blur print:hidden">
					<SidebarTrigger className="-ml-1" />
					<SafeRateLogo className="h-6 w-auto md:hidden" />
				</header>
				<div className="flex flex-1 flex-col gap-4 p-4">
					<Outlet />
				</div>
				<SiteFooter />
			</SidebarInset>
			<script
				// biome-ignore lint/security/noDangerouslySetInnerHtml: false positive
				dangerouslySetInnerHTML={{
					__html: `window.ENV = ${JSON.stringify(ENV)}`,
				}}
				nonce={nonce}
			/>
		</SidebarProvider>
	);
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />;
}
